/**
 * DataToolbar — reusable toolbar for search, sort, filter, and per-page controls.
 *
 * Declaratively configured via props. Works with useListState hook.
 */
import React, { useRef, useEffect, useState } from 'react'
import { Search, X, ArrowUpDown, SlidersHorizontal, ChevronDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import type { ListState } from '@/hooks/use-list-state'

export interface SortOption {
    label: string
    value: string
}

export interface FilterOption {
    label: string
    value: string
    icon?: React.ReactNode
    color?: string
}

export interface FilterConfig {
    key: string
    label: string
    options: FilterOption[]
}

export interface DataToolbarProps {
    listState: ListState
    searchPlaceholder?: string
    sortOptions?: SortOption[]
    filters?: FilterConfig[]
    totalItems?: number
    showPerPage?: boolean
    perPageOptions?: number[]
    extraFilters?: React.ReactNode
    children?: React.ReactNode
}

function FilterDropdown({
    config,
    selected,
    onToggle,
    onClear,
}: {
    config: FilterConfig
    selected: string[]
    onToggle: (value: string) => void
    onClear: () => void
}) {
    const [isOpen, setIsOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as HTMLElement)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-between whitespace-nowrap rounded-md border border-input bg-background px-3 py-2 text-sm h-9 min-w-[140px] hover:bg-accent/50 transition-colors"
            >
                <span className="flex items-center gap-1.5">
                    <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                    {selected.length > 0 ? (
                        <>
                            {config.label}
                            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px] font-medium">
                                {selected.length}
                            </Badge>
                        </>
                    ) : (
                        <span className="text-muted-foreground">{config.label}</span>
                    )}
                </span>
                <ChevronDown className={`ml-2 h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-56 rounded-md border bg-popover p-1 shadow-md animate-in fade-in-0 zoom-in-95">
                    <div className="max-h-60 overflow-y-auto">
                        {config.options.map((opt) => (
                            <label
                                key={opt.value}
                                className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer transition-colors"
                            >
                                <input
                                    type="checkbox"
                                    checked={selected.includes(opt.value)}
                                    onChange={() => onToggle(opt.value)}
                                    className="rounded border-gray-300"
                                />
                                {opt.color && (
                                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color }} />
                                )}
                                {opt.icon}
                                <span className="truncate">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                    {selected.length > 0 && (
                        <button
                            className="w-full text-xs text-muted-foreground hover:text-foreground py-1.5 border-t mt-1 transition-colors"
                            onClick={() => { onClear(); setIsOpen(false) }}
                        >
                            Clear {config.label}
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export function DataToolbar({
    listState,
    searchPlaceholder = 'Search...',
    sortOptions = [],
    filters = [],
    totalItems,
    showPerPage = true,
    perPageOptions = [5, 10, 25, 50],
    extraFilters,
    children,
}: DataToolbarProps) {
    const hasActiveFilters = Object.keys(listState.filters).length > 0

    return (
        <div className="flex flex-col gap-4 w-full">
            {/* Row 1: Search & Filters (Full Width) */}
            <div className="flex flex-wrap items-center gap-3 pb-1 w-full">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={searchPlaceholder}
                        value={listState.searchInput}
                        onChange={(e) => listState.setSearchInput(e.target.value)}
                        className="pl-8 h-9"
                    />
                    {listState.searchInput && (
                        <button
                            onClick={() => listState.setSearchInput('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>

                {/* Sort */}
                {sortOptions.length > 0 && (
                    <div className="flex items-center gap-1">
                        <Select
                            value={listState.sortBy || ''}
                            onValueChange={(val) => listState.setSortBy(val)}
                        >
                            <SelectTrigger className="h-9 w-[140px]">
                                <div className="flex items-center gap-1.5">
                                    <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                                    <SelectValue placeholder="Sort by" />
                                </div>
                            </SelectTrigger>
                            <SelectContent>
                                {sortOptions.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-9 w-9 p-0"
                            onClick={listState.toggleSortOrder}
                            title={listState.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                        >
                            <span className="text-xs font-mono">
                                {listState.sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                        </Button>
                    </div>
                )}

                {/* Filters */}
                {filters.map((filterConfig) => {
                    const selected = (() => {
                        const val = listState.filters[filterConfig.key]
                        if (!val) return []
                        return Array.isArray(val) ? val : [val]
                    })()

                    return (
                        <FilterDropdown
                            key={filterConfig.key}
                            config={filterConfig}
                            selected={selected}
                            onToggle={(value) => {
                                const current = selected
                                const next = current.includes(value)
                                    ? current.filter((v) => v !== value)
                                    : [...current, value]
                                listState.setFilter(filterConfig.key, next.length > 0 ? next : undefined)
                            }}
                            onClear={() => listState.setFilter(filterConfig.key, undefined)}
                        />
                    )
                })}

                {/* Extra custom filter components */}
                {extraFilters}

                {/* Clear all filters */}
                {hasActiveFilters && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 text-xs text-muted-foreground"
                        onClick={listState.clearFilters}
                    >
                        <X className="h-3 w-3 mr-1" />
                        Clear all
                    </Button>
                )}
            </div>

            {/* Row 2: Stats & Pagination Meta (Simple Right Aligned) */}
            <div className="flex items-center justify-end border-t border-gray-100 pt-0 gap-6">
                {/* Total count */}
                {totalItems !== undefined && (
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {totalItems} {totalItems === 1 ? 'result' : 'results'}
                    </span>
                )}

                {/* Per page */}
                {showPerPage && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Per page</span>
                        <Select value={String(listState.perPage)} onValueChange={(val) => listState.setPerPage(Number(val))}>
                            <SelectTrigger className="h-8 w-[65px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {perPageOptions.map((n) => (
                                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {children && (
                    <div className="flex items-center gap-2">
                        {children}
                    </div>
                )}
            </div>
        </div>
    )
}
